import React, { useMemo } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import type { ProductProductivityReport } from '@data';
import { useProductProductivityReport } from '@data/hooks/products';
import { Card, SectionHeading, SectionLabel } from '@app/components/core';
import { ShareLinesChart } from './charts';
import { Notice } from './QualityTab';
import { qualityPeriodLabel } from './sources/quality';
import {
  ProductivityFigure,
  ProductivityItem,
  ProductivityMix,
  ProductivityMixKey,
  ProductivityNotReportable,
  ProductivityReportView,
  ProductivityStat,
  isNotReportable,
  productivityView,
} from './sources/productivity';

const Legend = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <p className="text-xs leading-relaxed text-muted-foreground">{children}</p>
);

const Lead = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <p className="-mt-1 text-sm text-muted-foreground">{children}</p>
);

const HeaderFacts = ({ header }: { header: ProductivityReportView['header'] }): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Card className="flex flex-col gap-1">
      <p className="text-base font-semibold text-foreground">{header.title}</p>
      <p className="text-sm text-muted-foreground">
        {header.who} · {t('auditReports.productivity.measured')}{' '}
        <span className="font-semibold text-foreground">{header.measured}</span> ·{' '}
        <span className="font-semibold text-foreground">{header.mergedPullRequests}</span>{' '}
        {t('auditReports.productivity.mergedPullRequests')} · {header.repositories}
      </p>
    </Card>
  );
};

// The first tile is the headline, emphasised as render.py's lead-stat is.
const StatTile = ({ stat }: { stat: ProductivityStat }): JSX.Element => (
  <li className={cx('rounded-xl border p-4', stat.lead ? 'border-primary/20 bg-primary/5' : 'border-border bg-card')}>
    <p className={cx('text-2xl font-bold', stat.lead ? 'text-primary' : 'text-foreground')}>{stat.value}</p>
    <p className="mt-1 text-sm font-medium text-foreground">{stat.label}</p>
    <p className="mt-0.5 text-xs text-muted-foreground">{stat.sub}</p>
  </li>
);

const StepFigure = ({ figure }: { figure: ProductivityFigure }): JSX.Element => {
  const max = Math.max(...figure.bars.map(bar => bar.value), 0) || 1;
  return (
    <Card className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-base font-semibold text-foreground">{figure.title}</p>
          <p className="text-xs text-muted-foreground">{figure.unit}</p>
        </div>
        <span className="rounded-md bg-primary/10 px-2 py-0.5 text-lg font-bold text-primary">{figure.delta}</span>
      </div>
      <div className="flex h-40 items-end gap-4 border-b border-border">
        {figure.bars.map((bar, i) => {
          const last = i === figure.bars.length - 1;
          return (
            <div key={bar.name} className="flex h-full flex-1 flex-col items-center justify-end gap-1">
              <span className={cx('text-lg font-bold tabular-nums', last ? 'text-primary' : 'text-foreground')}>
                {bar.display}
              </span>
              <div
                className={cx('w-full max-w-28 rounded-t', last ? 'bg-primary' : 'bg-muted-foreground/30')}
                style={{ height: `${(bar.value / max) * 75}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="-mt-2 flex gap-4">
        {figure.bars.map((bar, i) => (
          <div key={bar.name} className="flex-1 text-center">
            <p className={cx('text-xs text-foreground', i === figure.bars.length - 1 && 'font-semibold')}>{bar.name}</p>
            <p className="text-[11px] text-muted-foreground">{bar.sub}</p>
          </div>
        ))}
      </div>
      <Legend>{figure.caption}</Legend>
    </Card>
  );
};

const MIX: Record<ProductivityMixKey, string> = {
  both: 'bg-foreground text-background',
  aionly: 'bg-primary text-primary-foreground',
  colonly: 'bg-muted-foreground/40 text-foreground',
  none: 'bg-muted text-foreground',
};

const MixBar = ({ mix }: { mix: ProductivityMix[] }): JSX.Element => (
  <div className="flex flex-col gap-2">
    <div className="flex h-7 overflow-hidden rounded-md text-xs font-semibold">
      {mix.map(part => (
        <span
          key={part.key}
          className={cx('flex items-center justify-center', MIX[part.key])}
          style={{ width: `${part.share}%` }}
        >
          {part.showInBar ? part.display : ''}
        </span>
      ))}
    </div>
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
      {mix.map(part => (
        <span key={part.key} className="inline-flex items-center gap-1.5">
          <span className={cx('size-2.5 rounded-sm', MIX[part.key])} />
          <span className="font-semibold text-foreground">{part.label}</span> {part.legend}
        </span>
      ))}
    </div>
  </div>
);

const ReviewFigure = ({ review }: { review: ProductivityReportView['review'] }): JSX.Element => {
  const { figure } = review;
  const index = (month: string): number => figure.months.findIndex(row => row.month === month);
  return (
    <Card className="flex flex-col gap-4">
      <p className="text-base font-semibold text-foreground">{figure.title}</p>
      <ShareLinesChart
        axis={figure.axis}
        lines={[
          {
            key: 'ai',
            label: figure.legend.ai,
            values: figure.months.map(row => row.aiShare),
            color: 'var(--primary)',
          },
          {
            key: 'colleague',
            label: figure.legend.colleague,
            values: figure.months.map(row => row.colleagueShare),
            color: 'var(--muted-foreground)',
            dashed: true,
          },
        ]}
        bands={figure.periods
          .map(period => ({ from: index(period.from), to: index(period.to), label: period.label }))
          .filter(band => band.from >= 0 && band.to >= band.from)}
        xLabels={figure.months.flatMap((row, i) =>
          row.month.endsWith('-01') ? [{ index: i, label: row.month.slice(0, 4) }] : [],
        )}
      />
      <MixBar mix={figure.mix} />
      <Legend>{figure.caption}</Legend>
    </Card>
  );
};

const GainsTable = ({ gains }: { gains: NonNullable<ProductivityReportView['gains']> }): JSX.Element => (
  <Card className="overflow-x-auto p-0">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {gains.columns.map((column, i) => (
            <th key={column} className={cx('px-4 py-3', i > 0 && i < 4 && 'text-right')}>
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {gains.rows.map(row => (
          <tr key={row.dimension} className="border-b border-border last:border-b-0">
            <td className="px-4 py-2.5 text-foreground">{row.dimension}</td>
            <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{row.before}</td>
            <td className="px-4 py-2.5 text-right tabular-nums text-foreground">{row.now}</td>
            <td
              className={cx(
                'px-4 py-2.5 text-right tabular-nums',
                row.evidence === 'next' && 'font-normal text-muted-foreground/70',
                row.evidence !== 'next' &&
                  (row.muted ? 'font-semibold text-muted-foreground' : 'font-semibold text-foreground'),
              )}
            >
              {row.change}
            </td>
            <td className="px-4 py-2.5">
              <span
                className={cx(
                  'rounded-md px-2 py-0.5 text-xs font-medium',
                  row.evidence === 'measured'
                    ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {row.evidence}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </Card>
);

const Definitions = ({ items }: { items: ProductivityItem[] }): JSX.Element => (
  <Card>
    <dl className="flex flex-col gap-4">
      {items.map(item => (
        <div key={item.title}>
          <dt className="text-sm font-semibold text-foreground">{item.title}</dt>
          <dd className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.body}</dd>
        </div>
      ))}
    </dl>
  </Card>
);

const ReportView = ({ view }: { view: ProductivityReportView }): JSX.Element => (
  <div className="flex flex-col gap-4">
    <HeaderFacts header={view.header} />

    <SectionLabel>{view.glance.title}</SectionLabel>
    <ul aria-label={view.glance.title} className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {view.glance.stats.map(stat => (
        <StatTile key={stat.label} stat={stat} />
      ))}
    </ul>

    {view.stepChange && (
      <>
        <SectionLabel>{view.stepChange.title}</SectionLabel>
        <Lead>{view.stepChange.lead}</Lead>
        {view.stepChange.figures.map(figure => (
          <StepFigure key={figure.title} figure={figure} />
        ))}
      </>
    )}

    <SectionLabel>{view.review.title}</SectionLabel>
    <Lead>{view.review.lead}</Lead>
    <ReviewFigure review={view.review} />

    {view.gains && (
      <>
        <SectionLabel>{view.gains.title}</SectionLabel>
        <Lead>{view.gains.lead}</Lead>
        <GainsTable gains={view.gains} />
      </>
    )}

    <SectionLabel>{view.measured.title}</SectionLabel>
    <Definitions items={view.measured.items} />

    <SectionLabel>{view.notClaiming.title}</SectionLabel>
    <Lead>{view.notClaiming.lead}</Lead>
    <Definitions items={view.notClaiming.items} />

    <Legend>{view.footer}</Legend>
  </div>
);

// The API logs the tooling detail; the client reads only that there is nothing to compare yet.
const NotReportableNotice = ({
  refusal,
  isClient,
}: {
  refusal: ProductivityNotReportable;
  isClient: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Notice>
      {t(`auditReports.productivity.notReportable.${refusal.reason}`)}
      {!isClient && <span className="mt-1 block">{t('auditReports.productivity.notReportable.setup')}</span>}
    </Notice>
  );
};

const isGithubUnreachable = (error: unknown): boolean =>
  (error as { response?: { data?: { reason?: string } } })?.response?.data?.reason === 'githubUnreachable';

/** The API's productivity view in Phill's own layout; a product with no productivity JSON yet shows `fallback`. */
const ProductivityTab = ({
  productId,
  report,
  isListLoading,
  isUnavailable = false,
  isClient = false,
  fallback,
  header,
  actions,
}: {
  productId?: string;
  report: ProductProductivityReport | null;
  isListLoading: boolean;
  /** The index could not be read, so whether this product has a report is unknown rather than no. */
  isUnavailable?: boolean;
  isClient?: boolean;
  /** Shown instead when the index is empty. */
  fallback: React.ReactNode;
  header?: React.ReactNode;
  actions?: React.ReactNode;
}): JSX.Element => {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useProductProductivityReport(productId, report?.year, report?.quarter);
  const view = useMemo(() => productivityView(data?.report), [data]);

  if (!isListLoading && !report && !isUnavailable) return <>{fallback}</>;

  const label = report ? qualityPeriodLabel(report) : '';
  let body = <Notice>{t('auditReports.loading')}</Notice>;
  if (!isListLoading && !report) body = <Notice>{t('auditReports.unavailable')}</Notice>;
  else if (isGithubUnreachable(error)) body = <Notice>{t('auditReports.unavailable')}</Notice>;
  else if (isError) body = <Notice>{t('auditReports.productivity.failed', { period: label })}</Notice>;
  else if (!isLoading && view)
    body = isNotReportable(view) ? (
      <NotReportableNotice refusal={view} isClient={isClient} />
    ) : (
      <ReportView view={view} />
    );

  return (
    <section className="flex flex-col gap-4">
      <div>
        {header ?? (
          <SectionHeading action={actions}>
            {t('auditReports.productivity.section')}
            {!!report && <span className="hidden print:inline">{` · ${label}`}</span>}
          </SectionHeading>
        )}
        {!!report && (
          <p className="-mt-1 text-sm text-muted-foreground">
            {t('auditReports.productivity.measuredTo', { period: label, date: report.generatedOn })}
          </p>
        )}
      </div>
      {body}
    </section>
  );
};

export default ProductivityTab;
